import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

const featuresMdUrl = `${process.env.PUBLIC_URL || ""}/features.md`;

interface MdProps {
  children?: React.ReactNode;
  [key: string]: unknown;
}

interface MdAnchorProps extends MdProps {
  href?: string;
}

const markdownComponents = {
  h1: ({ children, ...props }: MdProps) => (
    <Typography variant="h4" component="h1" gutterBottom fontWeight={600} {...props}>
      {children}
    </Typography>
  ),
  h2: ({ children, ...props }: MdProps) => (
    <Typography variant="h5" component="h2" gutterBottom fontWeight={600} sx={{ mt: 3 }} {...props}>
      {children}
    </Typography>
  ),
  h3: ({ children, ...props }: MdProps) => (
    <Typography variant="h6" component="h3" gutterBottom fontWeight={600} sx={{ mt: 2 }} {...props}>
      {children}
    </Typography>
  ),
  p: ({ children, ...props }: MdProps) => (
    <Typography variant="body1" paragraph {...props}>
      {children}
    </Typography>
  ),
  ul: ({ children, ...props }: MdProps) => (
    <Box component="ul" sx={{ pl: 2.5, mb: 1 }} {...props}>
      {children}
    </Box>
  ),
  ol: ({ children, ...props }: MdProps) => (
    <Box component="ol" sx={{ pl: 2.5, mb: 1 }} {...props}>
      {children}
    </Box>
  ),
  li: ({ children, ...props }: MdProps) => (
    <Typography component="li" variant="body1" sx={{ mb: 0.5 }} {...props}>
      {children}
    </Typography>
  ),
  a: ({ href, children, ...props }: MdAnchorProps) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" color="primary" underline="hover" {...props}>
      {children}
    </Link>
  ),
  strong: ({ children, ...props }: MdProps) => (
    <Typography component="span" fontWeight={600} {...props}>
      {children}
    </Typography>
  ),
  table: ({ children, ...props }: MdProps) => (
    <TableContainer sx={{ mb: 2 }}>
      <Table size="small" {...props}>
        {children}
      </Table>
    </TableContainer>
  ),
  thead: ({ children, ...props }: MdProps) => (
    <TableHead {...props}>{children}</TableHead>
  ),
  tbody: ({ children, ...props }: MdProps) => (
    <TableBody {...props}>{children}</TableBody>
  ),
  tr: ({ children, ...props }: MdProps) => (
    <TableRow {...props}>{children}</TableRow>
  ),
  th: ({ children, ...props }: MdProps) => (
    <TableCell component="th" variant="head" sx={{ fontWeight: 600 }} {...props}>
      {children}
    </TableCell>
  ),
  td: ({ children, ...props }: MdProps) => (
    <TableCell {...props}>{children}</TableCell>
  ),
  code: ({ children, ...props }: MdProps) => (
    <Box
      component="code"
      sx={{
        fontFamily: "monospace",
        fontSize: "0.9em",
        backgroundColor: "grey.100",
        color: "text.primary",
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "grey.300",
      }}
      {...props}
    >
      {children}
    </Box>
  ),
  pre: ({ children, ...props }: MdProps) => (
    <Box
      component="pre"
      sx={{
        fontFamily: "monospace",
        fontSize: "0.875rem",
        backgroundColor: "grey.100",
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 1,
        p: 2,
        overflow: "auto",
        mb: 2,
      }}
      {...props}
    >
      {children}
    </Box>
  ),
};

const FeaturesPage: React.FC = () => {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(featuresMdUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Fehler beim Laden: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setContent(text);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Unbekannter Fehler");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">Features-Datei konnte nicht geladen werden: {error}</Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        height: "calc(100vh - 80px)",
        overflowY: "auto",
        width: "100%",
        // Scrollbar sichtbar machen (GlobalStyles blenden sie sonst aus)
        "&::-webkit-scrollbar": { width: "8px" },
        "&::-webkit-scrollbar-track": { backgroundColor: "grey.200", borderRadius: 1 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "grey.400",
          borderRadius: 4,
          "&:hover": { backgroundColor: "grey.500" },
        },
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.08)",
      }}
    >
      <Paper elevation={0} sx={{ p: 3, maxWidth: 960, width: "100%", mx: "auto" }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownComponents as Components}>
          {content ?? ""}
        </ReactMarkdown>
      </Paper>
    </Box>
  );
};

export default FeaturesPage;
